import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { usePreferences } from '@/react-query/usePreferences';

import { useGetDateFNSLocale } from '../../../locales/helpers';
import { type Appointment } from '../../../react-query/useAppointments';
import { modeAtom, patientListViewAtom } from '../atoms';

import { getPatientAge } from './patientStripRedesigned/patientInfoSheet/PatientInfo';

export const AppointmentHeaderContent = ({
  appointment,
}: {
  appointment: Appointment;
}) => {
  const privacyOn = useSelector((state: any) => state.login?.user?.privacyOn);
  const view = useAtomValue(patientListViewAtom);
  const mode = useAtomValue(modeAtom);
  const { data: prefs } = usePreferences();
  const isHuddleDisabled = prefs?.huddleDisabled;

  const dateLocale = useGetDateFNSLocale();

  if (!appointment || view === 'MICRO') {
    return null;
  }
  if (view === 'REVIEW') {
    return (
      <div>
        <div className="vai-flex vai-items-baseline vai-flex-nowrap vai-gap-2 vai-text-black">
          {!isHuddleDisabled &&
            appointment.appointment.provider?.firstName &&
            appointment.appointment.provider?.lastName && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="vai-w-5 vai-h-5 vai-rounded-full vai-bg-green-600 vai-text-white vai-text-xs vai-cursor-pointer vai-flex vai-items-center vai-justify-center">
                      {appointment.appointment.provider.firstName[0] +
                        appointment.appointment.provider.lastName[0]}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>
                      {`${appointment.appointment.provider?.firstName} ${appointment.appointment.provider.lastName}`}
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          {!isHuddleDisabled && appointment.appointment.operatory?.name && (
            <div
              className="vai-text-sm vai-font-medium vai-self-center"
              data-text={appointment.appointment.operatory.name}
            >
              {appointment.appointment.operatory.name}
            </div>
          )}
          {appointment.appointment.start && (
            <div className="vai-mr-2 vai-whitespace-normal vai-text-slate-500 vai-font-normal vai-text-sm vai-self-center vai-pt-0.5">
              {format(
                new Date(appointment.appointment.start),
                mode === 'DAILY_HUDDLE' ? 'h:mmaaa' : 'PPp',
                {
                  locale: dateLocale,
                },
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="vai-flex vai-items-center vai-flex-nowrap vai-gap-2">
        {appointment.appointment.provider?.firstName &&
          appointment.appointment.provider?.lastName && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="vai-w-7 vai-h-7 vai-rounded-full vai-bg-indigo-400 vai-flex vai-items-center vai-justify-center vai-text-white vai-text-sm vai-p-0.5 vai-cursor-pointer">
                    {appointment.appointment.provider.firstName[0] +
                      appointment.appointment.provider.lastName[0]}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <span>
                    {`${appointment.appointment.provider?.firstName} ${appointment.appointment.provider.lastName}`}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        <div className="vai-flex vai-items-center vai-flex-row">
          {appointment.appointment.start && (
            <span className="vai-mr-2 vai-whitespace-normal vai-text-slate-500 vai-font-normal">
              {format(
                new Date(appointment.appointment.start),
                mode === 'DAILY_HUDDLE' ? 'p' : 'PPp',
                {
                  locale: dateLocale,
                },
              )}
            </span>
          )}
        </div>
      </div>
      <div className="vai-flex vai-items-center vai-flex-nowrap vai-gap-2">
        <span
          className="vai-font-bold vai-text-base vai-inline-block vai-whitespace-nowrap vai-overflow-hidden vai-text-ellipsis vai-flex vai-items-center"
          data-text={appointment.appointment.operatory?.name}
        >
          {appointment.appointment.operatory?.name}
        </span>
        <span className="vai-mr-2 vai-whitespace-normal vai-text-slate-500 vai-font-normal vai-flex vai-items-center">
          <span className={privacyOn ? `cell-age-private` : `cell-age-public`}>
            {appointment.patient?.dob &&
              privacyOn &&
              `Age ${getPatientAge(appointment.patient.dob)} `}
            {appointment.patient?.dob && !privacyOn && (
              <span className="vai-text-gray-400 vai-blur-sm vai-opacity-50">
                &#x2588;&#x2588;&#x2588;
              </span>
            )}
          </span>
          {/* Do not display gender until we figure out the quality of the data we receive - may need some mapping/filtering.
             {appointment.patient.gender} */}
        </span>
      </div>
    </>
  );
};
